/**
 * WifiTopologyCanvas — Diagrama interactivo de la red WiFi
 *
 * Filas (de arriba a abajo):
 *   1. Servidores RADIUS (y el servicio CMDB al que pertenecen).
 *   2. SSIDs, cada uno colgado de quien los difunde (controlador o AP).
 *   3. Controladores y APs independientes.
 *   4. APs gestionados por cada controlador.
 *
 * Interacción: pan/zoom, arrastrar tarjetas para recolocarlas (se guarda por
 * empresa) y clic en una tarjeta para abrir su ficha completa en el panel
 * lateral derecho.
 */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DocSmartButton from './DocSmartButton';
import LocationFilter, { matchesLocation } from './LocationFilter';
import WifiTopologyDoc from './WifiTopologyDoc';
import { buildLocationMap, locationPath, locationRootId } from '../utils/locationTree';
import './WifiTopology.css';

// ── Colors ───────────────────────────────────────────────────────────────────
const C = {
  ctrl:      { fill: 'rgba(139,92,246,.18)', stroke: '#818cf8', text: '#c4b5fd' },
  ap_up:     { fill: 'rgba(16,185,129,.15)', stroke: '#34d399', text: '#6ee7b7' },
  ap_down:   { fill: 'rgba(239,68,68,.15)',  stroke: '#f87171', text: '#fca5a5' },
  ap_unk:    { fill: 'var(--bg-input)',      stroke: 'var(--slate-300)', text: 'var(--text-secondary)' },
  orphan:    { fill: 'rgba(234,179,8,.15)',  stroke: '#fbbf24', text: '#fde68a' },
  ssid:      { fill: 'rgba(14,165,233,.15)', stroke: '#38bdf8', text: '#7dd3fc' },
  ssid_open: { fill: 'rgba(148,163,184,.15)', stroke: '#94a3b8', text: '#cbd5e1' },
  radius:    { fill: 'rgba(244,114,182,.16)', stroke: '#f472b6', text: '#f9a8d4' },
  radius_off:{ fill: 'rgba(148,163,184,.12)', stroke: '#94a3b8', text: '#cbd5e1' },
  sw:        { fill: 'rgba(45,212,191,.14)', stroke: '#2dd4bf', text: '#5eead4' },
};

// Tamaños de tarjeta
const CTRL_W = 200, CTRL_H = 78;
const AP_W   = 180, AP_H   = 88;
const SSID_W = 156, SSID_H = 52;
const RAD_W  = 200, RAD_H  = 68;
const SW_W   = 190, SW_H   = 76;

// Separaciones entre tarjetas / filas / secciones
const GAP_X       = 18;
const GAP_SECTION = 56;
const ROW_GAP     = 74;
const MARGIN_X    = 60;
const MARGIN_TOP  = 40;

// Rejilla de APs dentro de un grupo de ubicación.
const MAX_AP_COLS  = 6;   // APs por fila dentro de un grupo
const AP_ROW_GAP   = 22;  // entre filas de APs del mismo grupo
const GROUP_GAP_Y  = 76;  // entre bloques de ubicación (deja sitio a la caja)
const SWITCH_GAP   = 64;  // entre el último AP y la fila de switches
const BOX_PAD_X    = 14;  // aire lateral de la caja de ubicación
const BOX_HEAD_H   = 12;  // aire superior de la caja (la etiqueta va fuera)
const BOX_PAD_BOT  = 14;  // aire inferior de la caja de ubicación

const SECURITY_ICON = {
  open: '🔓',
  owe: '📶',
  wpa2_psk: '🔑',
  wpa3_psk: '🔑',
  wpa2_enterprise: '🛡️',
  wpa3_enterprise: '🛡️',
  wpa2_wpa3_enterprise: '🛡️',
};

function apColor(ap) {
  if (ap.status === 'decom') return C.ap_down;
  if (ap.status === 'active') return C.ap_up;
  if (ap.status === 'maintenance' || ap.status === 'planned') return C.ap_unk;
  return ap.controller_id ? C.ap_unk : C.orphan;
}

function ssidColor(ssid) {
  return ssid.security_mode === 'open' ? C.ssid_open : C.ssid;
}

const isEnterprise = (ssid) => String(ssid?.security_mode || '').includes('enterprise');

/** Dónde está / quién difunde un SSID. */
function ssidBroadcaster(ssid, controllers, aps) {
  if (ssid.controller_id) {
    const c = controllers.find(x => x.id === ssid.controller_id);
    return c ? `Controlador ${c.name}` : `Controlador #${ssid.controller_id}`;
  }
  if (ssid.access_point_id) {
    const a = aps.find(x => x.id === ssid.access_point_id);
    return a ? `AP ${a.name}` : `AP #${ssid.access_point_id}`;
  }
  return 'Sin asignar';
}

function apLocationLabel(ap) {
  const parts = [];
  if (ap.location_room)       parts.push(ap.location_room);
  else if (ap.location_name)  parts.push(ap.location_name);
  if (ap.location_floor)      parts.push(ap.location_floor);
  return parts.length ? parts.join(' · ') : 'Sin ubicación';
}

function controllerLocationLabel(ctrl) {
  const parts = [];
  if (ctrl.rack_name) {
    parts.push(ctrl.rack_position_u != null ? `${ctrl.rack_name} · U${ctrl.rack_position_u}` : ctrl.rack_name);
  }
  if (ctrl.rack_element_name) parts.push(ctrl.rack_element_name);
  return parts.length ? parts.join(' · ') : 'Ubicación no indicada';
}

function switchLocationLabel(sw) {
  const parts = [];
  if (sw.rack_name) {
    parts.push(sw.position_u != null ? `${sw.rack_name} · U${sw.position_u}` : sw.rack_name);
  }
  return parts.length ? parts.join(' · ') : 'Ubicación no indicada';
}

/** Resumen del cableado de un AP para su tarjeta: switch+puesto o último salto. */
function apWiringLabel(ap) {
  const up = ap.uplink;
  if (!up) return '🔌 sin cablear';
  if (up.switch) return `🔌 ${up.switch_port || '—'} · ${trunc(up.switch.name, 14)}`;
  const last = (up.hops || [])[up.hops.length - 1];
  if (last) return `🔌 ${last.label} · ${trunc(last.element_name || '', 12)}`;
  return '🔌 sin cablear';
}

/**
 * Ubicación con la que se agrupa un AP según el nivel elegido en la barra:
 *   'root'   → la raíz de su rama (la sede)
 *   'mother' → el primer nivel por debajo de la raíz (la oficina) ← defecto
 *   'exact'  → su propia ubicación (la más fina)
 */
function groupLocationIdFor(locationMap, locationId, level = 'mother') {
  if (locationId === null || locationId === undefined) return null;
  const rootId = locationRootId(locationMap, locationId);
  if (!rootId) return null;
  if (level === 'root') return rootId;
  if (level === 'exact') return String(locationId);
  let current = locationMap.get(String(locationId));
  let childId = current?.id != null ? String(current.id) : null;
  const seen = new Set();
  while (current && String(current.id) !== rootId) {
    const key = String(current.id);
    if (seen.has(key)) break;
    seen.add(key);
    childId = key;
    current = current.parent_id != null ? locationMap.get(String(current.parent_id)) : null;
  }
  return childId;
}

/**
 * Parte una lista de APs (ya ordenada) en bloques contiguos de una misma
 * ubicación madre. Cada bloque se pinta como una rejilla y se enmarca en su
 * caja, de modo que arrastrar la caja mueve todo el grupo.
 */
function splitApBlocks(apList, groupKeyOf) {
  const blocks = [];
  apList.forEach((ap) => {
    const key = groupKeyOf(ap) ?? null;
    const last = blocks[blocks.length - 1];
    if (last && last.key === key) last.aps.push(ap);
    else blocks.push({ key, aps: [ap] });
  });
  return blocks;
}

/** Grupo mayoritario de los APs de un switch (para meterlo en su caja). */
function majorityGroupKey(apList, apGroupKey) {
  const counts = new Map();
  apList.forEach((ap) => {
    const key = apGroupKey.get(ap.id) ?? null;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  let best = null;
  let bestCount = -1;
  counts.forEach((count, key) => {
    if (count > bestCount) { best = key; bestCount = count; }
  });
  return best;
}

/** Caja (bloque) donde caen más APs de un switch: ahí se dibuja el switch. */
function majorityBoxId(apList, apBoxId) {
  const counts = new Map();
  apList.forEach((ap) => {
    const boxId = apBoxId.get(ap.id);
    if (!boxId) return;
    counts.set(boxId, (counts.get(boxId) || 0) + 1);
  });
  let best = null;
  let bestCount = -1;
  counts.forEach((count, boxId) => {
    if (count > bestCount) { best = boxId; bestCount = count; }
  });
  return best;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Layout builder
// ─────────────────────────────────────────────────────────────────────────────
function buildLayout(controllers, aps, ssids, radiusServers, switchGroups = [], apGroupKey = new Map()) {
  const nodes = [];
  const edges = [];

  // SSIDs agrupados por quien los difunde
  const ssidsByCtrl = new Map();
  const ssidsByAp   = new Map();
  ssids.forEach(s => {
    if (s.controller_id) {
      if (!ssidsByCtrl.has(s.controller_id)) ssidsByCtrl.set(s.controller_id, []);
      ssidsByCtrl.get(s.controller_id).push(s);
    } else if (s.access_point_id) {
      if (!ssidsByAp.has(s.access_point_id)) ssidsByAp.set(s.access_point_id, []);
      ssidsByAp.get(s.access_point_id).push(s);
    }
  });

  // Secciones: una por controlador (con sus APs) y una por AP independiente.
  const sections = controllers.map(c => ({
    kind: 'ctrl', anchor: c,
    aps: aps.filter(a => a.controller_id === c.id),
    ssids: ssidsByCtrl.get(c.id) || [],
  }));
  aps.filter(a => !a.controller_id).forEach(a => {
    sections.push({ kind: 'ap', anchor: a, aps: [], ssids: ssidsByAp.get(a.id) || [] });
  });

  const hasAps   = sections.some(s => s.aps.length > 0);
  const hasSsids = sections.some(s => s.ssids.length > 0);
  const hasSw    = switchGroups.length > 0;

  // Filas de arriba a abajo: RADIUS → SSIDs → raíces (controlador/AP suelto) →
  // APs → switches (capa física, bajo los APs que cuelgan de ellos)
  let y = MARGIN_TOP;
  const RAD_Y = radiusServers.length ? y : null;
  if (RAD_Y != null) y += RAD_H + ROW_GAP;
  const SSID_Y = hasSsids ? y : null;
  if (SSID_Y != null) y += SSID_H + ROW_GAP;
  const HEAD_Y = y;
  y += Math.max(CTRL_H, AP_H) + ROW_GAP;
  const AP_Y = hasAps ? y : null;
  // Alto real de la zona de APs (ahora son varias filas): se calcula al
  // colocarlas y los switches se ponen debajo de todo.
  let maxApAreaH = 0;
  let switchY = hasSw ? y : null;

  const rowW = (count, w) => (count ? count * (w + GAP_X) - GAP_X : 0);

  // Dónde cae el centro de cada AP: sirve para poner cada switch justo debajo
  // de la nube de APs que cuelgan de él (así los cables van casi verticales).
  const apCenterX = new Map();
  // Caja (bloque) a la que pertenece cada AP: la caja se dibuja por bloque, no
  // por ubicación global, para que no abarque media pantalla cuando una misma
  // oficina tiene APs en dos controladores distintos.
  const apBoxId = new Map();

  let curX = MARGIN_X;
  const groupKeyOf = (ap) => apGroupKey.get(ap.id) ?? null;
  sections.forEach((s, sectionIdx) => {
    const isCtrl = s.kind === 'ctrl';
    const headW  = isCtrl ? CTRL_W : AP_W;
    const ssidRowW = rowW(s.ssids.length, SSID_W);

    // APs agrupados por ubicación madre: cada grupo es un bloque contiguo y se
    // reparte en rejilla (MAX_AP_COLS por fila). Así 27 APs no acaban en una
    // fila de 5 metros y cada grupo queda listo para enmarcarse en su caja.
    const blocks = splitApBlocks(s.aps, groupKeyOf).map(block => {
      const cols = Math.min(MAX_AP_COLS, block.aps.length);
      const rows = Math.ceil(block.aps.length / cols);
      return {
        ...block,
        cols,
        rows,
        w: rowW(cols, AP_W),
        h: rows * AP_H + (rows - 1) * AP_ROW_GAP,
      };
    });
    const apAreaW0 = blocks.length ? Math.max(...blocks.map(b => b.w)) : 0;
    // Muchos grupos pequeños no deben apilarse en una columna eterna: se
    // empaquetan en «estanterías» del ancho de una fila completa de APs. Con
    // un solo grupo (o grupos grandes) el resultado es el de siempre.
    const shelfW = blocks.length > 1
      ? Math.max(apAreaW0, rowW(MAX_AP_COLS, AP_W))
      : apAreaW0;
    const shelves = [];
    blocks.forEach(block => {
      const last = shelves[shelves.length - 1];
      if (last && last.w + GAP_X + block.w <= shelfW + 0.5) {
        last.blocks.push(block);
        last.w += GAP_X + block.w;
        last.h = Math.max(last.h, block.h);
      } else {
        shelves.push({ blocks: [block], w: block.w, h: block.h });
      }
    });
    const apAreaW = shelves.length ? Math.max(...shelves.map(sh => sh.w)) : 0;
    const apAreaH = shelves.reduce((sum, sh) => sum + sh.h, 0)
      + Math.max(0, shelves.length - 1) * GROUP_GAP_Y;

    const sectionW = Math.max(headW, apAreaW, ssidRowW);
    const centerX  = curX + sectionW / 2;

    if (isCtrl) {
      nodes.push({
        id: `ctrl-${s.anchor.id}`, kind: 'controller', data: s.anchor, ssids: s.ssids,
        x: centerX - CTRL_W / 2, y: HEAD_Y, w: CTRL_W, h: CTRL_H,
      });
    } else {
      nodes.push({
        id: `ap-${s.anchor.id}`, kind: 'ap', data: s.anchor, ssids: s.ssids, inherited: false,
        standalone: true, groupKey: groupKeyOf(s.anchor), boxId: `apbox-${sectionIdx}-head`,
        x: centerX - AP_W / 2, y: HEAD_Y, w: AP_W, h: AP_H,
      });
    }
    const headId = isCtrl ? `ctrl-${s.anchor.id}` : `ap-${s.anchor.id}`;

    // APs gestionados: heredan los SSID del controlador
    if (blocks.length && AP_Y != null) {
      let shelfY = AP_Y;
      let blockIdx = 0;
      shelves.forEach(shelf => {
        let shelfX = centerX - shelf.w / 2;
        shelf.blocks.forEach(block => {
          const boxId = `apbox-${sectionIdx}-${blockIdx}`;
          const startX = shelfX;
          block.aps.forEach((ap, i) => {
            const col = i % block.cols;
            const rowIdx = Math.floor(i / block.cols);
            const x = startX + col * (AP_W + GAP_X);
            const apY = shelfY + rowIdx * (AP_H + AP_ROW_GAP);
            nodes.push({
              id: `ap-${ap.id}`, kind: 'ap', data: ap, ssids: s.ssids, inherited: true,
              groupKey: block.key, boxId, x, y: apY, w: AP_W, h: AP_H,
            });
            apCenterX.set(ap.id, x + AP_W / 2);
            apBoxId.set(ap.id, boxId);
            edges.push({ from: headId, to: `ap-${ap.id}`, color: '#a78bfa', width: 1.6 });
          });
          shelfX += block.w + GAP_X;
          blockIdx += 1;
        });
        shelfY += shelf.h + GROUP_GAP_Y;
      });
      maxApAreaH = Math.max(maxApAreaH, apAreaH);
    }

    // SSIDs de la sección, justo encima de quien los difunde
    if (s.ssids.length) {
      const startX = centerX - ssidRowW / 2;
      s.ssids.forEach((ssid, i) => {
        nodes.push({
          id: `ssid-${ssid.id}`, kind: 'ssid', data: ssid,
          x: startX + i * (SSID_W + GAP_X), y: SSID_Y, w: SSID_W, h: SSID_H,
        });
        edges.push({ from: `ssid-${ssid.id}`, to: headId, color: '#38bdf8', width: 1.4, dashed: true });
      });
    }

    curX += sectionW + GAP_SECTION;
  });

  // Servidores RADIUS: fila superior, centrados sobre el conjunto
  if (radiusServers.length) {
    const contentW = Math.max(curX - GAP_SECTION - MARGIN_X, 0);
    const radRowW  = rowW(radiusServers.length, RAD_W);
    const startX   = MARGIN_X + Math.max((contentW - radRowW) / 2, 0);
    const usedIds  = new Set(ssids.filter(s => s.radius_server_id).map(s => s.radius_server_id));

    radiusServers.forEach((rs, i) => {
      nodes.push({
        id: `rad-${rs.id}`, kind: 'radius', data: rs, used: usedIds.has(rs.id),
        x: startX + i * (RAD_W + GAP_X), y: RAD_Y, w: RAD_W, h: RAD_H,
      });
    });
    ssids.forEach(s => {
      if (s.radius_server_id) {
        edges.push({
          from: `ssid-${s.id}`, to: `rad-${s.radius_server_id}`,
          color: '#f472b6', width: 1.4, dashed: true, label: '802.1X',
        });
      }
    });
  }

  // Switches: fila inferior, uno por switch con APs cableados a él. Se colocan
  // bajo el baricentro de sus APs (y nunca antes del anterior), que es lo que
  // deja los cables AP → switch sin cruces.
  if (switchGroups.length) {
    switchY = (AP_Y != null ? AP_Y : y) + maxApAreaH + SWITCH_GAP;
    const contentW = Math.max(curX - GAP_SECTION - MARGIN_X, 0);
    const swRowW   = rowW(switchGroups.length, SW_W);
    let lastRight  = MARGIN_X - GAP_X;
    // Si el baricentro empuja la fila más allá del contenido, se reparte.
    const desired = switchGroups.map((group) => {
      const xs = group.aps
        .map((ap) => apCenterX.get(ap.id))
        .filter((value) => typeof value === 'number');
      return xs.length ? xs.reduce((sum, value) => sum + value, 0) / xs.length : null;
    });
    const anyDesired = desired.some((value) => value !== null);
    const fallbackStart = MARGIN_X + Math.max((contentW - swRowW) / 2, 0);
    let cursor = fallbackStart;

    switchGroups.forEach((group, i) => {
      const center = anyDesired && desired[i] !== null
        ? desired[i]
        : cursor + SW_W / 2;
      const x = Math.max(center - SW_W / 2, lastRight + GAP_X, MARGIN_X);
      nodes.push({
        id: `sw-${group.switch.id}`, kind: 'switch', data: group.switch, aps: group.aps,
        groupKey: majorityGroupKey(group.aps, apGroupKey),
        boxId: majorityBoxId(group.aps, apBoxId),
        x, y: switchY, w: SW_W, h: SW_H,
      });
      lastRight = x + SW_W;
      cursor = x + SW_W + GAP_X;
      group.aps.forEach(ap => {
        edges.push({
          from: `ap-${ap.id}`, to: `sw-${group.switch.id}`,
          color: '#2dd4bf', width: 1.5, dashed: true,
          label: ap.uplink?.switch_port || '',
        });
      });
    });
  }

  return { nodes, edges };
}

// ─────────────────────────────────────────────────────────────────────────────
//  SVG Nodes
// ─────────────────────────────────────────────────────────────────────────────
const trunc = (value, max) => {
  const str = String(value ?? '');
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
};

/** Envoltorio común: rect + arrastre + doble clic para abrir la ficha. */
function NodeShell({ node, selected, col, onDown, onOpen, children }) {
  return (
    <g
      data-node={node.id}
      onMouseDown={e => onDown(e, node)}
      onDoubleClick={e => { e.stopPropagation(); onOpen(node); }}
      style={{ cursor: 'grab' }}
    >
      <rect x={node.x} y={node.y} width={node.w} height={node.h} rx={10}
        fill={col.fill} stroke={selected ? col.text : col.stroke}
        strokeWidth={selected ? 2.5 : 1.4}
      />
      {children}
    </g>
  );
}

function ControllerNode({ node, selected, onDown, onOpen }) {
  const col = C.ctrl;
  const d = node.data;
  const isActive = ['up', 'active', 'online'].includes(d.status);
  const ssidCount = (node.ssids || []).length;
  const apCount = d.ap_count ?? 0;
  return (
    <NodeShell node={node} selected={selected} col={col} onDown={onDown} onOpen={onOpen}>
      <circle cx={node.x + node.w - 12} cy={node.y + 12} r={5}
        fill={isActive ? '#22c55e' : '#ef4444'} />
      <text x={node.x + 9} y={node.y + 25} fontSize={14}>📡</text>
      <text x={node.x + 28} y={node.y + 25} fontSize={11.5} fontWeight={700} fill={col.text}>
        {trunc(d.name, 17)}
      </text>
      <text x={node.x + 10} y={node.y + 44} fontSize={9} fill={col.text} opacity={0.72}>
        📍 {trunc(controllerLocationLabel(d), 32)}
      </text>
      <text x={node.x + 10} y={node.y + 63} fontSize={9.5} fill={col.text} opacity={0.92}>
        🔵 {ssidCount} SSID{ssidCount === 1 ? '' : 's'} · 📶 {apCount} AP{apCount === 1 ? '' : 's'}
      </text>
    </NodeShell>
  );
}

function ApNode({ node, selected, onDown, onOpen }) {
  const col = apColor(node.data);
  const d = node.data;
  const count = (node.ssids || []).length;
  const secondLine = node.standalone
    ? `⚠️ sin controlador · 🔵 ${count} SSID${count === 1 ? '' : 's'}`
    : `🔵 ${count} SSID${count === 1 ? '' : 's'} heredado${count === 1 ? '' : 's'}`;
  const wired = !!d.uplink;
  return (
    <NodeShell node={node} selected={selected} col={col} onDown={onDown} onOpen={onOpen}>
      <text x={node.x + 9} y={node.y + 23} fontSize={13}>📶</text>
      <text x={node.x + 27} y={node.y + 23} fontSize={10.5} fontWeight={700} fill={col.text}>
        {trunc(d.name, 19)}
      </text>
      <text x={node.x + 9} y={node.y + 42} fontSize={9} fill={col.text} opacity={0.72}>
        📍 {trunc(apLocationLabel(d), 31)}
      </text>
      <text x={node.x + 9} y={node.y + 60} fontSize={9} fill={col.text} opacity={0.92}>
        {secondLine}
      </text>
      <text x={node.x + 9} y={node.y + 78} fontSize={8.5}
        fill={wired ? 'var(--text-secondary)' : 'var(--text-muted)'} opacity={0.95}>
        {apWiringLabel(d)}
      </text>
    </NodeShell>
  );
}

function SwitchNode({ node, selected, onDown, onOpen }) {
  const col = C.sw;
  const d = node.data;
  const aps = node.aps || [];
  const ports = aps.map(a => a.uplink?.switch_port).filter(Boolean);
  return (
    <NodeShell node={node} selected={selected} col={col} onDown={onDown} onOpen={onOpen}>
      <text x={node.x + 9} y={node.y + 23} fontSize={14}>🔀</text>
      <text x={node.x + 28} y={node.y + 23} fontSize={11} fontWeight={700} fill={col.text}>
        {trunc(d.name, 16)}
      </text>
      <text x={node.x + 9} y={node.y + 41} fontSize={9} fill={col.text} opacity={0.75}>
        {trunc(d.model || 'Switch', 22)}
      </text>
      <text x={node.x + 9} y={node.y + 56} fontSize={8.5} fill={col.text} opacity={0.72}>
        📍 {trunc(switchLocationLabel(d), 26)}
      </text>
      <text x={node.x + 9} y={node.y + 71} fontSize={9} fill={col.text} opacity={0.92}>
        🔌 {aps.length} AP{aps.length === 1 ? '' : 's'}
        {ports.length ? ` · ${trunc(ports.slice(0, 3).join(', '), 16)}` : ''}
      </text>
    </NodeShell>
  );
}

function SsidNode({ node, selected, onDown, onOpen }) {
  const d = node.data;
  const col = ssidColor(d);
  const ent = isEnterprise(d);
  const radiusLabel = d.radius_server_name || d.radius_server;
  return (
    <NodeShell node={node} selected={selected} col={col} onDown={onDown} onOpen={onOpen}>
      <text x={node.x + 9} y={node.y + 21} fontSize={12}>
        {SECURITY_ICON[d.security_mode] || '📶'}
      </text>
      <text x={node.x + 25} y={node.y + 21} fontSize={10.5} fontWeight={700} fill={col.text}>
        {trunc(d.name, 17)}{d.hidden ? ' 👁' : ''}
      </text>
      <text x={node.x + 9} y={node.y + 36} fontSize={8.5} fill={col.text} opacity={0.78}>
        {trunc(d.security_mode_label || d.security_mode || '', 24)}
      </text>
      <text x={node.x + 9} y={node.y + 48} fontSize={8.5} fill={ent ? '#f9a8d4' : col.text} opacity={0.95}>
        {ent
          ? `🔐 ${trunc(radiusLabel || 'RADIUS', 21)}`
          : `VLAN ${d.vlan_id ?? '—'} · ${trunc(d.band_filter_label || d.band_filter || '', 10)}`}
      </text>
    </NodeShell>
  );
}

function RadiusNode({ node, selected, onDown, onOpen, onOpenService }) {
  const d = node.data;
  const col = node.used ? C.radius : C.radius_off;
  return (
    <NodeShell node={node} selected={selected} col={col} onDown={onDown} onOpen={onOpen}>
      <text x={node.x + 9} y={node.y + 23} fontSize={14}>🔐</text>
      <text x={node.x + 28} y={node.y + 23} fontSize={11} fontWeight={700} fill={col.text}>
        {trunc(d.name, 16)}
      </text>
      <text x={node.x + 10} y={node.y + 41} fontSize={9} fill={col.text} opacity={0.82}>
        {trunc(d.host, 20)}:{d.auth_port}
      </text>
      {d.service_id ? (
        <g
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); onOpenService(d.service_id); }}
          style={{ cursor: 'pointer' }}
        >
          <title>Ver el servicio RADIUS en el diseñador</title>
          <text x={node.x + 10} y={node.y + 59} fontSize={9} fill="#c4b5fd"
            style={{ textDecoration: 'underline' }}>
            🧩 {trunc(d.service_name || `Servicio #${d.service_id}`, 18)} ↗
          </text>
        </g>
      ) : (
        <text x={node.x + 10} y={node.y + 59} fontSize={9} fill="var(--text-muted)">
          Sin servicio CMDB vinculado
        </text>
      )}
    </NodeShell>
  );
}

function Edge({ edge, nodeMap }) {
  const from = nodeMap[edge.from];
  const to   = nodeMap[edge.to];
  if (!from || !to) return null;
  // La arista se traza del borde más cercano al otro extremo, así funciona
  // igual hacia abajo (ctrl → AP) que hacia arriba (SSID → RADIUS).
  const fromIsLower = (from.y + from.h / 2) > (to.y + to.h / 2);
  const p1 = { x: from.x + from.w / 2, y: fromIsLower ? from.y : from.y + from.h };
  const p2 = { x: to.x   + to.w   / 2, y: fromIsLower ? to.y + to.h : to.y };
  const my = (p1.y + p2.y) / 2;
  const d  = `M${p1.x},${p1.y} C${p1.x},${my} ${p2.x},${my} ${p2.x},${p2.y}`;
  return (
    <g>
      <path d={d} fill="none" stroke={edge.color || '#a78bfa'} strokeWidth={edge.width || 1.5}
        opacity={0.55} strokeDasharray={edge.dashed ? '5 4' : undefined} />
      {edge.label && (
        <text x={(p1.x + p2.x) / 2} y={my} fontSize={8.5} fill={edge.color || '#a78bfa'}
          textAnchor="middle" opacity={0.9} paintOrder="stroke" stroke="var(--bg-card)" strokeWidth={3}>
          {edge.label}
        </text>
      )}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Panel lateral de detalle
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_LABEL = {
  up: 'Activo', active: 'Activo', online: 'Activo', down: 'Inactivo',
  inactive: 'Inactivo', maintenance: 'Mantenimiento', decom: 'Decomisado', planned: 'Planificado',
};

function row(label, value) { return { label, value }; }

function filterRows(rows) {
  return rows.filter(r => r.value !== null && r.value !== undefined && r.value !== '');
}

function boolLabel(v) { return v ? 'Sí' : 'No'; }

function radiusLocationText(rs, services) {
  if (!rs.service_id) return null;
  const svc = services.find(s => s.id === rs.service_id);
  return svc ? (svc.display_name || svc.name) : `Servicio #${rs.service_id}`;
}

/** Dónde corre el servicio RADIUS (nodos `runs_on` de su grafo en el diseñador). */
function ServiceRunsOn({ serviceId, companyId }) {
  const [state, setState] = useState({ loading: true, hosts: [], error: null });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, hosts: [], error: null });
    api.sdGetCiGraph('service', serviceId, companyId, { depth: 2, direction: 'out', storage: false })
      .then(p => {
        if (!alive) return;
        const graph = p?.data || p || {};
        const hosts = (graph.nodes || [])
          .filter(n => n.dir === 'out' && n.ci_type !== 'service')
          .map(n => n.name || `${n.ci_type} #${n.ci_id}`);
        setState({ loading: false, hosts: [...new Set(hosts)], error: null });
      })
      .catch(e => { if (alive) setState({ loading: false, hosts: [], error: e.message || 'error' }); });
    return () => { alive = false; };
  }, [serviceId, companyId]);

  if (state.loading) return <div className="wtopo-value" style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cargando ubicación…</div>;
  if (state.error) return <div className="wtopo-value" style={{ fontSize: 12, color: 'var(--text-muted)' }}>No se pudo consultar el diseñador.</div>;
  if (!state.hosts.length) {
    return (
      <div className="wtopo-note" style={{ marginTop: 4 }}>
        El servicio no tiene ningún “dónde corre” definido en el diseñador: ábrelo y conéctalo a su equipo.
      </div>
    );
  }
  return (
    <div className="wtopo-section">
      <p className="wtopo-section-title">📍 Dónde corre el servicio</p>
      {state.hosts.map(h => <span key={h} className="wtopo-chip">🖧 {h}</span>)}
    </div>
  );
}

function DetailPanel({ node, companyId, services, onClose, onOpenService, onNavigate }) {
  const d = node.data;
  // Tipo de elemento con el que se vinculan los documentos de la CMDB
  const docType = node.kind === 'controller' ? 'wifi_controller'
    : node.kind === 'ap' ? 'wifi_ap'
      : node.kind === 'radius' ? 'radius_server'
        : node.kind === 'ssid' ? 'wifi_ssid'
          : node.kind === 'switch' ? 'rack_element'
            : null;

  let rows = [];
  let chips = null;
  let title = '';
  let subtitle = '';
  let icon = '📦';
  let iconBg = 'rgba(139,92,246,.2)';

  if (node.kind === 'controller') {
    icon = '📡'; iconBg = 'rgba(139,92,246,.2)';
    title = d.name; subtitle = `Controlador · ${d.vendor_label || d.vendor || '—'}`;
    rows = filterRows([
      row('Nombre', d.name),
      row('Hostname', d.hostname),
      row('Fabricante', d.vendor_label || d.vendor),
      row('Modelo', d.model),
      row('Nº de serie', d.serial_number),
      row('Etiqueta', d.asset_tag),
      row('Firmware', d.firmware_version),
      row('Tipo', d.controller_type_label || d.controller_type),
      row('IP de gestión', d.management_ip),
      row('Máx. APs', d.max_aps),
      row('Máx. clientes', d.max_clients),
      row('APs gestionados', d.ap_count ?? 0),
      row('SSIDs difundidos', (node.ssids || []).length),
      row('Ubicación', controllerLocationLabel(d)),
      row('Estado', d.status_label || STATUS_LABEL[d.status] || d.status),
      row('Notas', d.notes),
    ]);
  } else if (node.kind === 'ap') {
    icon = '📶'; iconBg = 'rgba(16,185,129,.2)';
    title = d.name; subtitle = `Punto de acceso · ${d.vendor_label || d.vendor || '—'}`;
    rows = filterRows([
      row('Nombre', d.name),
      row('Controlador', d.controller_name || (d.controller_id ? `#${d.controller_id}` : 'Sin controlador (independiente)')),
      row('Fabricante', d.vendor_label || d.vendor),
      row('Modelo', d.model),
      row('Nº de serie', d.serial_number),
      row('Etiqueta', d.asset_tag),
      row('Firmware', d.firmware_version),
      row('MAC', d.mac_address),
      row('IP de gestión', d.management_ip),
      row('Estándar', d.wifi_standard_label || d.wifi_standard),
      row('Banda', d.band_label || d.band),
      row('Potencia TX', d.tx_power_dbm != null ? `${d.tx_power_dbm} dBm` : null),
      row('Máx. clientes', d.max_clients),
      row('PoE', boolLabel(d.poe_powered)),
      row('Montaje', d.mount_type_label || d.mount_type),
      row('Ubicación', apLocationLabel(d)),
      row('Planta', d.location_floor),
      row('Estancia', d.location_room),
      row('Estado', d.status_label || STATUS_LABEL[d.status] || d.status),
      row('Notas', d.notes),
    ]);
    chips = (
      <>
        <div className="wtopo-section">
          <p className="wtopo-section-title">
            {node.inherited ? '🔵 SSIDs heredados del controlador' : '🔵 SSIDs propios'}
          </p>
          {(node.ssids || []).length
            ? node.ssids.map(s => <span key={s.id} className="wtopo-chip wtopo-chip-ssid">📶 {s.name}</span>)
            : <span className="wtopo-hint">Ninguno</span>}
        </div>
        <div className="wtopo-section">
          <p className="wtopo-section-title">🔌 Cableado</p>
          {d.uplink ? (
            <>
              <div className="wtopo-rows">
                <div className="wtopo-row">
                  <span className="wtopo-label">Puerto del AP</span>
                  <span className="wtopo-value">
                    {d.uplink.port_label}{d.uplink.speed_mbps ? ` · ${d.uplink.speed_mbps} Mbps` : ''}
                  </span>
                </div>
                <div className="wtopo-row">
                  <span className="wtopo-label">Switch</span>
                  <span className="wtopo-value">{d.uplink.switch ? d.uplink.switch.name : '—'}</span>
                </div>
                <div className="wtopo-row">
                  <span className="wtopo-label">Puerto del switch</span>
                  <span className="wtopo-value">{d.uplink.switch_port || '—'}</span>
                </div>
                {d.uplink.switch ? (
                  <div className="wtopo-row">
                    <span className="wtopo-label">Ubicación del switch</span>
                    <span className="wtopo-value">{switchLocationLabel(d.uplink.switch)}</span>
                  </div>
                ) : null}
              </div>
              <p className="wtopo-hint" style={{ marginTop: 6 }}>
                {[`AP ${d.uplink.port_label}`, ...(d.uplink.hops || []).map(h => `${h.element_name || ''} ${h.label || ''}`.trim())].join(' → ')}
              </p>
            </>
          ) : (
            <span className="wtopo-hint">Este AP no tiene ningún cable registrado.</span>
          )}
        </div>
      </>
    );
  } else if (node.kind === 'switch') {
    icon = '🔀'; iconBg = 'rgba(45,212,191,.2)';
    title = d.name; subtitle = `Switch · ${d.model || '—'}`;
    rows = filterRows([
      row('Nombre', d.name),
      row('Modelo', d.model),
      row('Ubicación', switchLocationLabel(d)),
      row('APs conectados', (node.aps || []).length),
    ]);
    chips = (
      <div className="wtopo-section">
        <p className="wtopo-section-title">🔌 APs y puertos</p>
        {(node.aps || []).length
          ? node.aps.map(a => (
              <span key={a.id} className="wtopo-chip">
                📶 {a.name}{a.uplink?.switch_port ? ` · ${a.uplink.switch_port}` : ''}
              </span>
            ))
          : <span className="wtopo-hint">Ninguno</span>}
      </div>
    );
  } else if (node.kind === 'ssid') {
    icon = SECURITY_ICON[d.security_mode] || '📶'; iconBg = 'rgba(14,165,233,.2)';
    title = d.name; subtitle = `SSID · ${d.security_mode_label || d.security_mode || ''}`;
    rows = filterRows([
      row('SSID', d.name),
      row('Difundido por', d.broadcaster),
      row('Seguridad', d.security_mode_label || d.security_mode),
      row('RADIUS', d.radius_server_name || d.radius_server),
      row('Puerto auth', (d.radius_server_id || isEnterprise(d)) ? d.radius_port : null),
      row('Puerto accounting', d.radius_acct_port),
      row('VLAN', d.vlan_id),
      row('Bandas', d.band_filter_label || d.band_filter),
      row('Oculto', boolLabel(d.hidden)),
      row('Aislamiento de clientes', boolLabel(d.client_isolation)),
      row('Estado', d.status_label || STATUS_LABEL[d.status] || d.status),
      row('Notas', d.notes),
    ]);
  } else if (node.kind === 'radius') {
    icon = '🔐'; iconBg = 'rgba(244,114,182,.2)';
    title = d.name; subtitle = `Servidor RADIUS · ${d.host}`;
    rows = filterRows([
      row('Nombre', d.name),
      row('Host / IP', d.host),
      row('Puerto auth (UDP)', d.auth_port),
      row('Puerto accounting (UDP)', d.acct_port),
      row('Servicio CMDB', radiusLocationText(d, services)),
      row('SSIDs que lo usan', d.ssid_count ?? 0),
      row('Estado', STATUS_LABEL[d.status] || d.status),
      row('Descripción', d.description),
    ]);
    chips = <ServiceRunsOn serviceId={d.service_id} companyId={companyId} />;
  }

  return (
    <aside className="wtopo-aside">
      <div className="wtopo-inner">
        <div className="wtopo-head">
          <span className="wtopo-icon" style={{ background: iconBg }}>{icon}</span>
          <div className="wtopo-title">
            <h3 title={title}>{title}</h3>
            <p>{subtitle}</p>
          </div>
          <button className="wtopo-x" onClick={onClose} title="Cerrar">✕</button>
        </div>

        <div className="wtopo-rows">
          {rows.map(r => (
            <div key={r.label} className="wtopo-row">
              <span className="wtopo-label">{r.label}</span>
              <span className="wtopo-value">{String(r.value)}</span>
            </div>
          ))}
        </div>

        {chips}

        <div className="wtopo-actions">
          {node.kind === 'controller' && (
            <button className="wtopo-btn" onClick={() => onNavigate(`/wifi/controllers?destacar=${d.id}`)}>
              Abrir ficha ↗
            </button>
          )}
          {node.kind === 'ap' && (
            <button className="wtopo-btn" onClick={() => onNavigate(`/wifi/aps?destacar=${d.id}`)}>
              Abrir ficha ↗
            </button>
          )}
          {(node.kind === 'ap' && d.uplink?.switch) && (
            <button className="wtopo-btn" onClick={() => onNavigate('/infrastructure/switches')}>
              🔀 Ver {trunc(d.uplink.switch.name, 18)} ↗
            </button>
          )}
          {node.kind === 'ap' && d.location_id && (
            <button className="wtopo-btn" onClick={() => onNavigate(`/wifi/map?ap=${d.id}`)}>
              🗺 Ver en el plano ↗
            </button>
          )}
          {node.kind === 'switch' && (
            <button className="wtopo-btn" onClick={() => onNavigate('/infrastructure/switches')}>
              Ver switches ↗
            </button>
          )}
          {node.kind === 'ssid' && d.controller_id && (
            <button className="wtopo-btn" onClick={() => onNavigate(`/wifi/controllers?destacar=${d.controller_id}`)}>
              Ver controlador ↗
            </button>
          )}
          {node.kind === 'ssid' && d.access_point_id && (
            <button className="wtopo-btn" onClick={() => onNavigate(`/wifi/aps?destacar=${d.access_point_id}`)}>
              Ver AP ↗
            </button>
          )}
          {node.kind === 'ssid' && d.radius_server_id && (
            <button className="wtopo-btn" onClick={() => onNavigate('/wifi/radius-servers', { state: { openId: d.radius_server_id } })}>
              Ver servidor RADIUS ↗
            </button>
          )}
          {node.kind === 'radius' && (
            <button className="wtopo-btn" onClick={() => onNavigate('/wifi/radius-servers', { state: { openId: d.id } })}>
              Abrir ficha ↗
            </button>
          )}
          {(node.kind === 'radius' && d.service_id) && (
            <button className="wtopo-btn wtopo-btn-primary" onClick={() => onOpenService(d.service_id)}>
              🧩 Ver servicio en el diseñador ↗
            </button>
          )}
          {docType && (
            <div className="wtopo-section">
              <p className="wtopo-section-title">📄 Documentación</p>
              <div className="wtopo-smartdoc">
                <DocSmartButton
                  elementType={docType}
                  elementId={d.id}
                  elementName={d.name}
                  companyId={companyId}
                />
              </div>
            </div>
          )}
          <button className="wtopo-btn" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main
// ─────────────────────────────────────────────────────────────────────────────
export default function WifiTopologyCanvas({ companyId }) {
  const nav = useNavigate();
  const [controllers,  setControllers]  = useState([]);
  const [aps,          setAps]          = useState([]);
  const [ssids,        setSsids]        = useState([]);
  const [radiusServers, setRadiusServers] = useState([]);
  const [services,     setServices]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedId,   setSelectedId]   = useState(null);
  const [showSwitches, setShowSwitches] = useState(true);   // capa física
  const [locationFilter, setLocationFilter] = useState([]);
  // Nivel de agrupación de los APs en el grafo: raíz de la rama, «madre»
  // (primer nivel bajo la raíz, el valor por defecto) o la ubicación exacta.
  const [groupLevel, setGroupLevel] = useState('mother');
  const [locations, setLocations] = useState([]);
  const [showDoc,      setShowDoc]      = useState(false);  // borrador de documentación
  const [pos,          setPos]          = useState({});   // id → {x,y} (recolocaciones)

  const [vp,  setVp]  = useState({ x: 0, y: 0, scale: 1 });
  const dragging = useRef(null);   // paneo del lienzo
  const nodeDrag = useRef(null);   // arrastre de una tarjeta
  const groupDrag = useRef(null);  // arrastre de una caja de ubicación
  const userAdjusted = useRef(false);  // el usuario ya movió la vista/tarjetas
  const svgRef   = useRef();
  const vpRef    = useRef(vp);
  vpRef.current  = vp;
  const posRef   = useRef(pos);
  posRef.current = pos;

  const posKey = `wifiTopoPos:${companyId || 'none'}`;
  // OJO: el filtro es POR EMPRESA. Con una clave global, una selección hecha
  // en otra empresa ocultaba todos los APs al cambiar de empresa.
  const locKey = `wifiTopoLoc:${companyId || 'none'}`;
  const groupKeyStore = `wifiTopoGroup:${companyId || 'none'}`;

  // ── Carga de datos ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      api.getWifiControllers(companyId),
      api.getWifiAPs(companyId, { uplink: 1 }),
      api.getWifiSSIDs(companyId),
      api.getWifiRadiusServers(companyId),
    ]).then(([c, a, s, r]) => {
      setControllers(c.data || []);
      setAps(a.data || []);
      setSsids(s.data || []);
      setRadiusServers(r.data || []);
    }).catch(() => {}).finally(() => setLoading(false));

    // Los servicios solo se usan para etiquetar el RADIUS: si falla, no rompe.
    api.getServices(companyId)
      .then(list => setServices(Array.isArray(list) ? list : (list?.data || [])))
      .catch(() => setServices([]));

    api.getLocations('', companyId)
      .then(res => setLocations(Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []))
      .catch(() => setLocations([]));
  }, [companyId]);

  // Recolocaciones guardadas por empresa
  useEffect(() => {
    setSelectedId(null);
    try {
      setPos(JSON.parse(localStorage.getItem(`wifiTopoPos:${companyId || 'none'}`) || '{}') || {});
      const storedFilter = JSON.parse(localStorage.getItem(`wifiTopoLoc:${companyId || 'none'}`) || '[]');
      setLocationFilter(Array.isArray(storedFilter) ? storedFilter : []);
      const storedLevel = localStorage.getItem(`wifiTopoGroup:${companyId || 'none'}`);
      setGroupLevel(['none', 'root', 'mother', 'exact'].includes(storedLevel) ? storedLevel : 'mother');
    } catch {
      setPos({});
      setLocationFilter([]);
      setGroupLevel('mother');
    }
  }, [companyId]);

  // Si una ubicación del filtro ya no existe (borrada o de otra empresa), se
  // quita: si no, el grafo aparecería vacío sin explicación.
  useEffect(() => {
    if (!locations.length || !locationFilter.length) return;
    const ids = new Set(locations.map(loc => loc.id));
    const valid = locationFilter.filter(id => ids.has(id));
    if (valid.length !== locationFilter.length) setLocationFilter(valid);
  }, [locations, locationFilter]);

  // ── Layout ─────────────────────────────────────────────────────────────────
  // APs que pasan el filtro de ubicación (los controladores y SSID no se
  // filtran: son la red lógica, no están en una sala).
  const visibleAps = useMemo(() => {
    if (!locationFilter.length) return aps;
    // Un AP sin ubicación propia cuenta con la de su switch: si no, al filtrar
    // por zona desaparecían todos los APs aunque su switch sí estuviera.
    return aps.filter(ap => matchesLocation(
      locations,
      locationFilter,
      ap.location_id ?? ap.uplink?.switch?.location_id ?? null,
    ));
  }, [aps, locationFilter, locations]);

  // Mapa y grupos de ubicación. Cada AP se cuelga de su ubicación «madre» (el
  // primer nivel por debajo de la raíz de su rama), que es lo que define los
  // grupos del grafo: «oficina Barcelona», «oficina Girona»…
  const locationMap = useMemo(() => buildLocationMap(locations), [locations]);

  const apGroupKey = useMemo(() => {
    const map = new Map();
    aps.forEach(ap => {
      const locationId = ap.location_id ?? ap.uplink?.switch?.location_id ?? null;
      map.set(ap.id, groupLocationIdFor(locationMap, locationId, groupLevel));
    });
    return map;
  }, [aps, locationMap, groupLevel]);

  // Metadatos de cada grupo (etiqueta corta + ruta completa), en orden de
  // organigrama, para que las cajas salgan siempre en el mismo orden.
  const groupInfo = useMemo(() => {
    const entries = new Map();
    visibleAps.forEach(ap => {
      const key = apGroupKey.get(ap.id) ?? null;
      if (entries.has(key)) return;
      const loc = key != null ? locationMap.get(String(key)) : null;
      const path = loc ? (locationPath(locationMap, loc.id) || loc.name) : 'Sin ubicación';
      entries.set(key, {
        key,
        label: loc?.name || 'Sin ubicación',
        path,
        order: loc ? path : '\uffff',
      });
    });
    return new Map([...entries.entries()].sort((a, b) =>
      String(a[1].order).localeCompare(String(b[1].order), 'es', { sensitivity: 'base', numeric: true })
    ));
  }, [visibleAps, apGroupKey, locationMap]);

  const groupRank = useMemo(() => {
    const rank = new Map();
    let index = 0;
    groupInfo.forEach((_info, key) => { rank.set(key, index); index += 1; });
    return rank;
  }, [groupInfo]);

  // APs que cuelgan de cada ubicación contando descendientes: así el filtro
  // demuestra que marcar una ruta madre incluye todo lo que tiene por debajo.
  const locationCounts = useMemo(() => {
    const counts = {};
    aps.forEach(ap => {
      const startId = ap.location_id ?? ap.uplink?.switch?.location_id ?? null;
      if (startId === null || startId === undefined) return;
      let current = locationMap.get(String(startId));
      const seen = new Set();
      while (current) {
        const key = String(current.id);
        if (seen.has(key)) break;
        seen.add(key);
        counts[key] = (counts[key] || 0) + 1;
        current = current.parent_id != null ? locationMap.get(String(current.parent_id)) : null;
      }
    });
    return counts;
  }, [aps, locationMap]);

  // Capa física: switches agrupando los APs cableados a cada uno.
  // Orden: primero por zona (para que cada contenedor siga siendo contiguo) y
  // dentro de la zona por el **baricentro** de sus APs, que es lo que hace que
  // los cables AP → switch salgan casi en paralelo en vez de cruzarse.
  // Se hacen dos pasadas: la segunda ya mide el baricentro con los APs
  // colocados, que es lo que de verdad reduce los cruces.
  const layoutOrder = useMemo(() => {
    const switchIdOf = (ap) => ap.uplink?.switch?.id ?? null;

    const buildGroups = (apList) => {
      const map = new Map();
      apList.forEach((ap, index) => {
        const switchId = switchIdOf(ap);
        if (!switchId) return;
        if (!map.has(switchId)) {
          map.set(switchId, { switch: ap.uplink.switch, aps: [], sum: 0 });
        }
        const group = map.get(switchId);
        group.aps.push(ap);
        group.sum += index;
      });
      return [...map.values()]
        .map((group) => ({ ...group, barycenter: group.sum / group.aps.length }))
        // Orden puramente geométrico: cada switch va donde caen sus APs. Si se
        // agrupara primero por ubicación, un switch de otra zona se vería
        // empujado al final y sus cables cruzarían medio mapa.
        .sort((a, b) => {
          if (a.barycenter !== b.barycenter) return a.barycenter - b.barycenter;
          const la = a.switch.location_name || '\uffff';
          const lb = b.switch.location_name || '\uffff';
          if (la !== lb) return la.localeCompare(lb);
          return String(a.switch.name || '').localeCompare(String(b.switch.name || ''));
        });
    };

    const sortAps = (apList, rank) => [...apList].sort((a, b) => {
      // 1.º por grupo de ubicación: los APs de una misma caja van seguidos.
      const ga = groupRank.has(apGroupKey.get(a.id) ?? null)
        ? groupRank.get(apGroupKey.get(a.id) ?? null) : Number.MAX_SAFE_INTEGER;
      const gb = groupRank.has(apGroupKey.get(b.id) ?? null)
        ? groupRank.get(apGroupKey.get(b.id) ?? null) : Number.MAX_SAFE_INTEGER;
      if (ga !== gb) return ga - gb;
      const ra = rank.has(switchIdOf(a)) ? rank.get(switchIdOf(a)) : Number.MAX_SAFE_INTEGER;
      const rb = rank.has(switchIdOf(b)) ? rank.get(switchIdOf(b)) : Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });

    const groups1 = buildGroups(visibleAps);
    const rank1 = new Map(groups1.map((group, index) => [group.switch.id, index]));
    const aps1 = sortAps(visibleAps, rank1);

    const groups2 = buildGroups(aps1);
    const rank2 = new Map(groups2.map((group, index) => [group.switch.id, index]));
    const aps2 = sortAps(visibleAps, rank2);

    return { switchGroups: groups2, switchRank: rank2, apsOrdered: aps2 };
  }, [visibleAps, groupRank, apGroupKey]);

  const { switchGroups, apsOrdered } = layoutOrder;

  // Los controladores también se ordenan por donde caen sus APs, así el árbol
  // entero se lee de izquierda a derecha sin saltos.
  const controllersOrdered = useMemo(() => {
    const acc = new Map();
    apsOrdered.forEach((ap, index) => {
      if (!ap.controller_id) return;
      const cur = acc.get(ap.controller_id) || { sum: 0, count: 0 };
      cur.sum += index;
      cur.count += 1;
      acc.set(ap.controller_id, cur);
    });
    const rankOf = (ctrl) => {
      const cur = acc.get(ctrl.id);
      if (!cur || !cur.count) return Number.MAX_SAFE_INTEGER;
      return cur.sum / cur.count;
    };
    return [...controllers].sort((a, b) => {
      const ra = rankOf(a);
      const rb = rankOf(b);
      if (ra !== rb) return ra - rb;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [controllers, apsOrdered]);

  const wiredAps = visibleAps.filter(a => a.uplink).length;

  const { nodes: baseNodes, edges } = useMemo(
    () => buildLayout(
      controllersOrdered,
      apsOrdered,
      ssids,
      radiusServers,
      showSwitches ? switchGroups : [],
      apGroupKey,
    ),
    [controllersOrdered, apsOrdered, ssids, radiusServers, switchGroups, showSwitches, apGroupKey]
  );

  const nodes = useMemo(
    () => baseNodes.map(n => (pos[n.id] ? { ...n, ...pos[n.id] } : n)),
    [baseNodes, pos]
  );

  // Cajas por bloque de ubicación: envuelven SOLO los APs de ese bloque (y los
  // switches que les dan servicio). Si una oficina tiene APs en dos
  // controladores salen dos cajas, cada una con su gente, en vez de una caja
  // gigante que lo abarcaba todo. La cabecera es arrastrable: mueve el bloque.
  const locationBoxes = useMemo(() => {
    if (groupLevel === 'none') return [];
    const boxes = new Map();
    const ensure = (boxId, groupKey) => {
      if (!boxes.has(boxId)) {
        const info = groupInfo.get(groupKey) || { key: groupKey, label: 'Sin ubicación', path: 'Sin ubicación' };
        boxes.set(boxId, {
          boxId, key: groupKey, label: info.label, path: info.path,
          x1: Infinity, x2: -Infinity, y1: Infinity, y2: -Infinity,
          apCount: 0, switchCount: 0, memberIds: [],
        });
      }
      return boxes.get(boxId);
    };
    nodes.forEach(n => {
      if (n.kind !== 'ap' && n.kind !== 'switch') return;
      const box = ensure(n.boxId ?? `nobox-${n.id}`, n.groupKey ?? null);
      box.x1 = Math.min(box.x1, n.x);
      box.x2 = Math.max(box.x2, n.x + n.w);
      box.y1 = Math.min(box.y1, n.y);
      box.y2 = Math.max(box.y2, n.y + n.h);
      if (n.kind === 'ap') box.apCount += 1;
      else box.switchCount += 1;
      box.memberIds.push(n.id);
    });
    return [...boxes.values()]
      .filter(box => box.apCount > 0)
      .map(box => ({
        id: `locbox-${box.boxId}`,
        kind: 'locbox',
        key: box.key,
        label: box.label,
        path: box.path,
        apCount: box.apCount,
        switchCount: box.switchCount,
        memberIds: box.memberIds,
        x: box.x1 - BOX_PAD_X,
        y: box.y1 - BOX_HEAD_H,
        w: box.x2 - box.x1 + BOX_PAD_X * 2,
        h: box.y2 - box.y1 + BOX_HEAD_H + BOX_PAD_BOT,
      }))
      // Las cajas grandes primero: las pequeñas quedan por delante si anidan.
      .sort((a, b) => (b.w * b.h) - (a.w * a.h));
  }, [nodes, groupInfo, groupLevel]);

  // El SSID conoce quién lo difunde (para el panel y el buscador de ubicación)
  const nodeMap = useMemo(() => {
    const indexed = nodes.map(n => {
      if (n.kind !== 'ssid') return n;
      return { ...n, data: { ...n.data, broadcaster: ssidBroadcaster(n.data, controllers, aps) } };
    });
    return Object.fromEntries(indexed.map(n => [n.id, n]));
  }, [nodes, controllers, aps]);

  const selected = selectedId ? (nodeMap[selectedId] || null) : null;

  const bbox = useMemo(() => {
    if (!nodes.length) return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    const xs = nodes.flatMap(n => [n.x, n.x + n.w]);
    const ys = nodes.flatMap(n => [n.y, n.y + n.h]);
    return { minX: Math.min(...xs) - 40, minY: Math.min(...ys) - 60, maxX: Math.max(...xs) + 60, maxY: Math.max(...ys) + 60 };
  }, [nodes]);

  const handleFit = useCallback(() => {
    if (!svgRef.current) return;
    userAdjusted.current = false;
    const { width: W, height: H } = svgRef.current.getBoundingClientRect();
    const sceneW = bbox.maxX - bbox.minX;
    const sceneH = bbox.maxY - bbox.minY;
    const scale  = Math.min(1.4, Math.min(W / sceneW, H / sceneH) * 0.9);
    setVp({ x: W / 2 - (bbox.minX + sceneW / 2) * scale, y: H / 2 - (bbox.minY + sceneH / 2) * scale, scale });
  }, [bbox]);

  // Encajar la vista cuando aparecen los datos y cada vez que el lienzo cambia
  // de tamaño (menú lateral, redimensionado…), salvo si el usuario ya ha
  // paneado/zoomeado/recolocado tarjetas: entonces se respeta su vista.
  const fitRef = useRef(null);
  fitRef.current = handleFit;
  useEffect(() => {
    const el = svgRef.current;
    if (loading || !el || !nodes.length) return;
    let timer = setTimeout(() => { if (!userAdjusted.current) fitRef.current?.(); }, 60);
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => { if (!userAdjusted.current) fitRef.current?.(); }, 120);
    });
    ro.observe(el);
    return () => { ro.disconnect(); clearTimeout(timer); };
  }, [loading, nodes.length]);

  // ── Zoom / paneo ───────────────────────────────────────────────────────────
  const onWheel = useCallback(e => {
    e.preventDefault();
    userAdjusted.current = true;
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    setVp(v => {
      const newScale = Math.max(0.2, Math.min(3, v.scale * factor));
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return v;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      return { scale: newScale, x: mx - (mx - v.x) * (newScale / v.scale), y: my - (my - v.y) * (newScale / v.scale) };
    });
  }, []);

  // Listener NATIVO de la rueda con `passive: false` (ver VirtTopologyCanvas):
  // React la registra como pasiva y el preventDefault no sirve de nada.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel, loading, nodes.length]);

  const toScene = useCallback((clientX, clientY) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const { x, y, scale } = vpRef.current;
    return { x: (clientX - rect.left - x) / scale, y: (clientY - rect.top - y) / scale };
  }, []);

  const onMouseDown = useCallback(e => {
    if (e.target.closest?.('[data-node]')) return;   // las tarjetas se arrastran aparte
    e.preventDefault();
    userAdjusted.current = true;
    dragging.current = { startX: e.clientX - vpRef.current.x, startY: e.clientY - vpRef.current.y };
  }, []);

  const onNodeDown = useCallback((e, node) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    userAdjusted.current = true;
    const p = toScene(e.clientX, e.clientY);
    nodeDrag.current = { node, dx: p.x - node.x, dy: p.y - node.y, moved: false };
  }, [toScene]);

  // Arrastrar la cabecera de una caja de ubicación mueve TODO el grupo: se
  // capturan las posiciones actuales de sus miembros y se aplica el mismo
  // desplazamiento a cada una.
  const onGroupDown = useCallback((e, box) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    userAdjusted.current = true;
    const p = toScene(e.clientX, e.clientY);
    const members = box.memberIds
      .map(id => nodeMap[id])
      .filter(Boolean)
      .map(n => ({ id: n.id, x: n.x, y: n.y }));
    groupDrag.current = {
      members,
      boxX: box.x,
      boxY: box.y,
      dx: p.x - box.x,
      dy: p.y - box.y,
      moved: false,
    };
  }, [toScene, nodeMap]);

  const onMouseMove = useCallback(e => {
    const g = groupDrag.current;
    if (g) {
      const p = toScene(e.clientX, e.clientY);
      const dx = (p.x - g.dx) - g.boxX;
      const dy = (p.y - g.dy) - g.boxY;
      if (!g.moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) g.moved = true;
      if (g.moved) {
        const next = {};
        g.members.forEach(m => { next[m.id] = { x: m.x + dx, y: m.y + dy }; });
        setPos(prev => ({ ...prev, ...next }));
      }
      return;
    }
    const d = nodeDrag.current;
    if (d) {
      const p = toScene(e.clientX, e.clientY);
      const nx = p.x - d.dx;
      const ny = p.y - d.dy;
      if (!d.moved && (Math.abs(nx - d.node.x) > 3 || Math.abs(ny - d.node.y) > 3)) d.moved = true;
      if (d.moved) setPos(prev => ({ ...prev, [d.node.id]: { x: nx, y: ny } }));
      return;
    }
    if (!dragging.current) return;
    const d2 = dragging.current;
    // Valores capturados ANTES del updater: React puede ejecutarlo cuando el
    // arrastre ya ha terminado (ref null) y reventaba al sacar el dibujo fuera.
    if (e.buttons === 0) { dragging.current = null; return; }
    const px = e.clientX - d2.startX;
    const py = e.clientY - d2.startY;
    setVp(v => ({ ...v, x: px, y: py }));
  }, [toScene]);

  const stopDrag = useCallback(() => {
    const g = groupDrag.current;
    if (g) {
      groupDrag.current = null;
      if (g.moved) {
        try { localStorage.setItem(posKey, JSON.stringify(posRef.current)); } catch { /* sin persistencia */ }
      }
    }
    const d = nodeDrag.current;
    if (d) {
      nodeDrag.current = null;
      if (d.moved) {
        try { localStorage.setItem(posKey, JSON.stringify(posRef.current)); } catch { /* sin persistencia */ }
      } else {
        setSelectedId(d.node.id);
      }
    }
    dragging.current = null;
  }, [posKey]);

  const resetLayout = useCallback(() => {
    setPos({});
    try { localStorage.removeItem(posKey); } catch { /* sin persistencia */ }
    userAdjusted.current = false;
    setTimeout(() => fitRef.current?.(), 30);
  }, [posKey]);

  // Cambiar el nivel de agrupación reordena el grafo: las posiciones manuales
  // guardadas dejarían las tarjetas dispersas, así que se reinician.
  const changeGroupLevel = useCallback((level) => {
    setGroupLevel(level);
    try { localStorage.setItem(groupKeyStore, level); } catch { /* sin persistencia */ }
    setPos({});
    try { localStorage.removeItem(posKey); } catch { /* sin persistencia */ }
    userAdjusted.current = false;
    setTimeout(() => fitRef.current?.(), 60);
  }, [groupKeyStore, posKey]);

  const openServiceDesigner = useCallback(serviceId => {
    nav(`/services/designer?service=${serviceId}`);
  }, [nav]);

  // Doble clic sobre una tarjeta = abrir su ficha en el panel
  const onOpenNode = useCallback(node => setSelectedId(node.id), []);

  const legend = [
    { c: C.radius.stroke,  label: 'RADIUS' },
    { c: C.ssid.stroke,    label: 'SSID' },
    { c: C.ctrl.stroke,    label: 'Controlador' },
    { c: C.ap_up.stroke,   label: 'AP activo' },
    { c: C.orphan.stroke,  label: 'AP sin controlador' },
    { c: C.ap_unk.stroke,  label: 'AP en mant./prev.' },
    { c: C.ap_down.stroke, label: 'AP decomisado' },
    { c: C.sw.stroke,      label: 'Switch' },
  ];

  const enterpriseCount = ssids.filter(isEnterprise).length;

  if (!companyId) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Selecciona una empresa.</div>;

  return (
    <div className="wtopo-page">
      {/* Toolbar */}
      <div className="wtopo-toolbar">
        <span className="wtopo-page-title">📦 Topología WiFi</span>
        <span className="wtopo-hint">
          Arrastra una tarjeta o la cabecera de un grupo (📍) para recolocarlos · clic para ver la ficha
        </span>
        <div style={{ flexGrow: 1 }} />
        <div className="wtopo-legend">
          {legend.map(l => (
            <span key={l.label} className="wtopo-legend-item">
              <span className="wtopo-legend-dot" style={{ background: l.c }} />
              {l.label}
            </span>
          ))}
        </div>
        <button onClick={handleFit} style={btnStyle('#0891b2')}>⤢ Ajustar</button>
        <button onClick={resetLayout} style={btnStyle('#6b7280')} title="Vuelve a la distribución automática">↺ Reordenar</button>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}
          title="Nivel de ubicación con el que se agrupan los APs en el grafo"
        >
          📦 Agrupar por
          <select
            value={groupLevel}
            onChange={e => changeGroupLevel(e.target.value)}
            style={{
              padding: '4px 8px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              background: 'var(--bg-input)', color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
            }}
          >
            <option value="mother">Madre (oficina)</option>
            <option value="root">Sede (raíz)</option>
            <option value="exact">Ubicación del AP</option>
            <option value="none">Sin agrupar</option>
          </select>
        </label>
        <LocationFilter
          companyId={companyId}
          value={locationFilter}
          onChange={(ids) => {
            setLocationFilter(ids);
            try { localStorage.setItem(locKey, JSON.stringify(ids)); } catch { /* noop */ }
          }}
          extraItems={locations}
          showPath
          counts={locationCounts}
        />
        {switchGroups.length > 0 && (
          <button
            onClick={() => setShowSwitches(s => !s)}
            style={btnStyle(showSwitches ? '#0d9488' : '#6b7280', !showSwitches)}
            title="Mostrar u ocultar la capa de switches y puertos"
          >
            🔀 Switches ({switchGroups.length}){showSwitches ? ' ✓' : ''}
          </button>
        )}
        <button onClick={() => setVp(v => ({ ...v, scale: Math.min(3, v.scale * 1.2) }))} style={btnStyle('#6b7280')}>+</button>
        <button onClick={() => setVp(v => ({ ...v, scale: Math.max(0.2, v.scale / 1.2) }))} style={btnStyle('#6b7280')}>−</button>
        <button onClick={() => nav('/wifi/dashboard')} style={btnStyle('#374151', true)}>📊 Dashboard WiFi</button>
        <button onClick={() => nav('/wifi/controllers')} style={btnStyle('#374151', true)}>📡 Controladores</button>
        <button onClick={() => nav('/wifi/aps')} style={btnStyle('#374151', true)}>📶 APs</button>
        <button onClick={() => nav('/wifi/radius-servers')} style={btnStyle('#374151', true)}>🔐 RADIUS</button>
        <button onClick={() => { setSelectedId(null); setShowDoc(true); }} style={btnStyle('#0d9488', !showDoc)}
          title="Generar un borrador de documentación del WiFi con lo que hay configurado">
          📄 Documentación
        </button>
      </div>

      {/* Stats bar */}
      {!loading && (
        <div className="wtopo-statbar">
          <span>📡 <strong>{controllers.length}</strong> controladores</span>
          <span>
            📶 <strong>{visibleAps.length}</strong> APs
            {locationFilter.length ? ` de ${aps.length}` : ''}
          </span>
          <span>🗺️ <strong>{aps.filter(a => a.location_id).length}</strong> con ubicación</span>
          <span>✅ <strong>{aps.filter(a => a.status === 'active').length}</strong> activos</span>
          <span>🛠️ <strong>{aps.filter(a => a.status === 'maintenance' || a.status === 'planned').length}</strong> en mant./prev.</span>
          <span>❌ <strong>{aps.filter(a => a.status === 'decom').length}</strong> decomisados</span>
          <span>🔵 <strong>{ssids.length}</strong> SSIDs ({new Set(ssids.map(s => s.name)).size} únicos)</span>
          <span>🛡️ <strong>{enterpriseCount}</strong> con RADIUS</span>
          <span>🔐 <strong>{radiusServers.length}</strong> servidores RADIUS</span>
          <span>🔌 <strong>{wiredAps}</strong> APs cableados de {aps.length}</span>
          <span>🔀 <strong>{switchGroups.length}</strong> switches</span>
          <span>
            📦 <strong>{new Set(locationBoxes.map(b => b.key ?? 'none')).size}</strong> grupo{new Set(locationBoxes.map(b => b.key ?? 'none')).size === 1 ? '' : 's'} de ubicación
          </span>
        </div>
      )}

      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
          <span className="spinner-border spinner-border-sm me-2" /> Cargando topología WiFi…
        </div>
      )}

      {!loading && nodes.length === 0 && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 48, opacity: 0.3 }}>📶</div>
          <div style={{ fontWeight: 600 }}>Sin infraestructura WiFi registrada</div>
          <div style={{ fontSize: 13 }}>Añade controladores, APs y SSIDs para ver la topología</div>
        </div>
      )}

      {!loading && nodes.length > 0 && (
        <svg
          ref={svgRef}
          className="wtopo-canvas"
          style={{ flex: 1, cursor: dragging.current ? 'grabbing' : 'grab', userSelect: 'none' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          onDoubleClick={() => setSelectedId(null)}
        >
          <defs>
            <pattern id="wgrid" width="30" height="30" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="var(--border)" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#wgrid)" />

          <g transform={`translate(${vp.x},${vp.y}) scale(${vp.scale})`}>
            {/* Marcos de grupo por bloque de ubicación. El borde usa
                `non-scaling-stroke` para no desaparecer al alejar. */}
            {locationBoxes.map(box => (
              <rect
                key={box.id}
                x={box.x} y={box.y} width={box.w} height={box.h} rx={18}
                fill="rgba(var(--accent-rgb, 99, 102, 241), .10)"
                stroke="var(--accent)" strokeOpacity={0.8} strokeWidth={1.8}
                strokeDasharray="8 6" vectorEffect="non-scaling-stroke"
                style={{ pointerEvents: 'none' }}
              />
            ))}

            {edges.map((e, i) => <Edge key={`${e.from}-${e.to}-${i}`} edge={e} nodeMap={nodeMap} />)}

            {nodes.filter(n => n.kind === 'radius').map(n => (
              <RadiusNode key={n.id} node={n} selected={selectedId === n.id}
                onDown={onNodeDown} onOpen={onOpenNode} onOpenService={openServiceDesigner} />
            ))}

            {nodes.filter(n => n.kind === 'ssid').map(n => (
              <SsidNode key={n.id} node={n} selected={selectedId === n.id}
                onDown={onNodeDown} onOpen={onOpenNode} />
            ))}

            {nodes.filter(n => n.kind === 'controller').map(n => (
              <ControllerNode key={n.id} node={n} selected={selectedId === n.id}
                onDown={onNodeDown} onOpen={onOpenNode} />
            ))}

            {nodes.filter(n => n.kind === 'ap').map(n => (
              <ApNode key={n.id} node={n} selected={selectedId === n.id}
                onDown={onNodeDown} onOpen={onOpenNode} />
            ))}

            {nodes.filter(n => n.kind === 'switch').map(n => (
              <SwitchNode key={n.id} node={n} selected={selectedId === n.id}
                onDown={onNodeDown} onOpen={onOpenNode} />
            ))}
          </g>

          {/* Etiquetas de grupo: en espacio de PANTALLA (no escalan con el
              zoom) y por encima de todo, para que siempre se lean y no tapen
              las tarjetas. Son la zona arrastrable de cada caja. Con el zoom
              muy alejado se ocultan: si no, se apilan unas sobre otras. */}
          {vp.scale >= 0.28 && locationBoxes.map(box => {
            const sx = vp.x + box.x * vp.scale;
            const sy = vp.y + box.y * vp.scale;
            const label = trunc(box.label, 30);
            const chipW = Math.max(140, label.length * 7.4 + 150);
            return (
              <g
                key={`chip-${box.id}`}
                className="wtopo-group-head"
                data-group={box.id}
                onMouseDown={e => onGroupDown(e, box)}
                transform={`translate(${sx}, ${sy - 30})`}
              >
                <title>{`${box.path} — arrastra para mover el grupo`}</title>
                <rect
                  x={0} y={0} rx={9} width={chipW} height={26}
                  fill="var(--bg-surface)" stroke="var(--accent)" strokeOpacity={0.95} strokeWidth={1.2}
                />
                <text x={10} y={18} fontSize={12} fontWeight={700} fill="var(--accent)">
                  📍 {label}
                  <tspan fill="var(--text-secondary)" fontWeight={500}>
                    {'  '}{box.apCount} AP{box.apCount !== 1 ? 's' : ''}
                    {box.switchCount ? ` · ${box.switchCount} switch${box.switchCount !== 1 ? 'es' : ''}` : ''}
                  </tspan>
                </text>
              </g>
            );
          })}
        </svg>
      )}

      {selected && (
        <DetailPanel
          node={selected}
          companyId={companyId}
          services={services}
          onClose={() => setSelectedId(null)}
          onOpenService={openServiceDesigner}
          onNavigate={(to, opts) => nav(to, opts)}
        />
      )}

      {showDoc && (
        <WifiTopologyDoc
          companyId={companyId}
          controllers={controllers}
          aps={aps}
          ssids={ssids}
          radiusServers={radiusServers}
          services={services}
          onClose={() => setShowDoc(false)}
          onNavigate={to => nav(to)}
        />
      )}
    </div>
  );
}

function btnStyle(color, outline = false) {
  return {
    padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
    background: outline ? 'var(--bg-input)' : color,
    color: outline ? 'var(--text-secondary)' : '#fff',
    border: `1px solid ${outline ? 'var(--slate-300)' : color}`,
  };
}
